import { Button } from "@bindery/ui/components/ui/button";
import { cn } from "@bindery/ui/lib/utils";
import {
  Check,
  ChevronDown,
  ChevronUp,
  Delete,
  Eraser,
  Sparkles,
} from "lucide-react";
import {
  useEffect,
  useEffectEvent,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { actionForKeyPress, editWord, type KeyboardAction } from "./keys";
import { letterOptions, type KeyboardModel } from "./model";
import { wordChoices, type CompletedVia, type SuggestWord } from "./suggestion";
import { useSuggestion } from "./use-suggestion";

export interface DynamicKeyboardProps {
  readonly model: KeyboardModel;
  readonly onWordComplete: (word: string, via: CompletedVia) => void;
  readonly suggest?: SuggestWord;
  /** The text the word goes into, as `suggest` sees it; a change re-asks. */
  readonly context?: string;
  readonly optionCount?: number;
  readonly className?: string;
}

const DEFAULT_OPTION_COUNT = 4;

const isTypingElsewhere = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement &&
  (target.isContentEditable || target.matches("input, textarea, select"));

const holdsFocus = (root: HTMLElement): boolean => {
  const active = document.activeElement;
  return (
    active instanceof HTMLElement &&
    root.contains(active) &&
    !active.matches(":disabled")
  );
};

export function DynamicKeyboard({
  model,
  onWordComplete,
  suggest,
  context,
  optionCount = DEFAULT_OPTION_COUNT,
  className,
}: DynamicKeyboardProps) {
  const [word, setWord] = useState("");
  const [moreOpen, setMoreOpen] = useState(false);
  const moreId = useId();
  const rootRef = useRef<HTMLFieldSetElement>(null);
  const focusWasInside = useRef(false);

  const { primary, more } = letterOptions(model, word, optionCount);
  const question = { prefix: word, context };
  const { answer, forget } = useSuggestion(suggest, question);
  const { finish: finishAs, chip } = wordChoices(
    question,
    suggest !== undefined,
    answer,
  );
  const canFinish = finishAs !== null;
  const offered = [...primary, ...more];

  const noteFocus = () => {
    focusWasInside.current =
      rootRef.current !== null && holdsFocus(rootRef.current);
  };

  const finish = (completed: string, via: CompletedVia) => {
    noteFocus();
    onWordComplete(completed, via);
    forget();
    setWord("");
    setMoreOpen(false);
  };

  const act = (action: KeyboardAction) => {
    if (action.kind === "submit") {
      if (finishAs !== null) finish(word, finishAs);
      return;
    }
    noteFocus();
    setWord(editWord(word, action));
    if (action.kind === "letter") setMoreOpen(false);
  };

  // A tap can unmount or disable the focused control (a tray letter, the
  // chip, Done); the browser then drops focus to <body>, which strands a
  // switch or screen-reader user. Catch it here and hold focus on the group.
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!focusWasInside.current || root === null) return;
    focusWasInside.current = false;
    if (!holdsFocus(root)) root.focus();
  });

  const onPhysicalKey = useEffectEvent((event: KeyboardEvent) => {
    if (event.defaultPrevented || isTypingElsewhere(event.target)) return;
    const action = actionForKeyPress(event, offered);
    if (action === null) return;
    // Enter on a focused button is that button's own activation; claiming it
    // for submit would leave keyboard users unable to press any key.
    if (action.kind === "submit" && event.target instanceof HTMLButtonElement)
      return;
    event.preventDefault();
    act(action);
  });

  useEffect(() => {
    const listener = (event: KeyboardEvent) => onPhysicalKey(event);
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, []);

  return (
    <fieldset
      ref={rootRef}
      aria-label="Letter keyboard"
      tabIndex={-1}
      className={cn(
        "flex w-full min-w-0 max-w-xl flex-col gap-6 rounded-3xl border bg-card p-4 text-card-foreground shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-ring sm:p-6",
        className,
      )}
    >
      <WordDisplay word={word} complete={finishAs === "word"} />

      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap justify-center gap-3">
          {primary.map((letter, slot) => (
            <LetterKey
              // Keyed by position, not letter, so the focused key survives
              // each letter and only its label changes.
              key={slot}
              letter={letter}
              onPick={() => act({ kind: "letter", letter })}
              className="size-18 rounded-2xl text-5xl font-semibold sm:size-24 sm:text-6xl"
            />
          ))}
          {primary.length === 0 && (
            <p className="text-muted-foreground py-6 text-center text-sm">
              No letter can follow.
            </p>
          )}
        </div>

        {more.length > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="lg"
            aria-expanded={moreOpen}
            aria-controls={moreId}
            onClick={() => setMoreOpen((open) => !open)}
            className="text-muted-foreground self-center"
          >
            {moreOpen ? <ChevronUp /> : <ChevronDown />}
            {moreOpen ? "fewer letters" : "more letters"}
          </Button>
        )}

        {moreOpen && more.length > 0 && (
          <div
            id={moreId}
            className="bg-muted/60 animate-in fade-in slide-in-from-top-2 flex flex-wrap justify-center gap-2 rounded-2xl p-3 motion-reduce:animate-none"
          >
            {more.map((letter) => (
              <LetterKey
                key={letter}
                letter={letter}
                variant="secondary"
                onPick={() => act({ kind: "letter", letter })}
                className="size-14 rounded-xl text-3xl font-medium sm:size-16 sm:text-4xl"
              />
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            aria-label="Backspace"
            disabled={word === ""}
            onClick={() => act({ kind: "backspace" })}
            className="size-14 rounded-2xl [&_svg:not([class*='size-'])]:size-6"
          >
            <Delete />
          </Button>
          <Button
            type="button"
            variant="outline"
            aria-label="Clear the word"
            disabled={word === ""}
            onClick={() => act({ kind: "clear" })}
            className="size-14 rounded-2xl [&_svg:not([class*='size-'])]:size-6"
          >
            <Eraser />
          </Button>
        </div>

        <div className="flex gap-2">
          {chip !== null && (
            <Button
              type="button"
              variant="secondary"
              aria-label={`Finish the word as ${chip}`}
              onClick={() => finish(chip, "chip")}
              className="h-14 rounded-2xl px-4 text-xl tracking-wide lowercase [&_svg:not([class*='size-'])]:size-5"
            >
              <Sparkles className="text-muted-foreground" />
              {chip}
            </Button>
          )}
          <Button
            type="button"
            disabled={!canFinish}
            onClick={() => act({ kind: "submit" })}
            className={cn(
              "h-14 rounded-2xl px-5 text-lg [&_svg:not([class*='size-'])]:size-6",
              canFinish &&
                "bg-chart-2 text-primary-foreground hover:bg-chart-2/90",
            )}
          >
            <Check />
            Done
          </Button>
        </div>
      </div>
    </fieldset>
  );
}

interface LetterKeyProps {
  readonly letter: string;
  readonly onPick: () => void;
  readonly variant?: "default" | "secondary";
  readonly className?: string;
}

function LetterKey({
  letter,
  onPick,
  variant = "default",
  className,
}: LetterKeyProps) {
  return (
    <Button
      type="button"
      variant={variant}
      onClick={onPick}
      className={cn(
        "lowercase leading-none transition-transform active:scale-95 motion-reduce:transform-none",
        className,
      )}
    >
      <span
        key={letter}
        className="animate-in fade-in zoom-in-95 motion-reduce:animate-none"
      >
        {letter}
      </span>
    </Button>
  );
}

interface WordDisplayProps {
  readonly word: string;
  readonly complete: boolean;
}

function WordDisplay({ word, complete }: WordDisplayProps) {
  return (
    <div
      className={cn(
        "flex min-h-24 items-center justify-center rounded-2xl border-2 border-dashed px-4 py-3 transition-colors sm:min-h-28",
        complete && "border-solid border-chart-2 bg-chart-2/10",
      )}
    >
      <output aria-live="polite" className="sr-only">
        {word === ""
          ? "No letters yet"
          : complete
            ? `${word}, a whole word`
            : word}
      </output>
      {word === "" ? (
        <span aria-hidden className="text-muted-foreground text-base">
          Pick a letter to start a word
        </span>
      ) : (
        <span
          aria-hidden
          className="flex items-end gap-1 text-6xl leading-none font-semibold tracking-wide lowercase sm:text-7xl"
        >
          {Array.from(word).map((letter, index) => (
            <span
              key={`${index}-${letter}`}
              className="animate-in fade-in slide-in-from-bottom-2 motion-reduce:animate-none"
            >
              {letter}
            </span>
          ))}
          {!complete && (
            <span className="bg-primary mb-1 ml-1 h-12 w-1 animate-pulse rounded-full motion-reduce:animate-none sm:h-14" />
          )}
        </span>
      )}
    </div>
  );
}
